"use client";
import { FlashList } from "@shopify/flash-list";
import { MutableRefObject, ReactNode, useCallback, useMemo } from "react";
import {
	Gesture,
	GestureDetector,
	GestureStateChangeEvent,
	GestureUpdateEvent,
	PanGestureChangeEventPayload,
	PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import Animated, {
	AnimatedRef,
	SharedValue,
	runOnJS,
	scrollTo,
	useAnimatedReaction,
	useAnimatedStyle,
	useFrameCallback,
	useSharedValue,
	withSpring,
} from "react-native-reanimated";
import { SpringConfig } from "react-native-reanimated/lib/typescript/animation/springUtils";
import isTouchScreen from "src/constants/isTouchScreen";
import native from "src/constants/native";
import { DragSelectionWrapper } from "src/contexts/DragSelectionContext";
import haptic from "src/helpers/haptic";
import useDevice from "src/hooks/useDevice";
import useDim from "src/hooks/useDim";
import { FixedPosition } from "src/styles/position";
import { useSafeArea } from "src/swsh-native";
import { updateDragArea } from "./updateDragArea";

export type Measurements = {
	top: number;
	left: number;
	width: number;
	height: number;
};

export type DragArea =
	| ({
			startScrollOffset: number;
			startX: number;
			startY: number;
			endX: number;
			endY: number;
			proximityToBottom?: number;
			proximityToTop?: number;
	  } & Measurements)
	| null;

export type DragSelectionProviderProps = {
	enabled: boolean;
	scrollOffset: SharedValue<number>;
	setShouldScroll: (isScrolling: boolean) => void;
	animatedRef: AnimatedRef<FlashList<any>>;
	panRef: MutableRefObject<any>;
	isOverrideScrollingSV: SharedValue<boolean>;
	children?: ReactNode;
};

const DragSelectionProviderContent = ({
	enabled,
	scrollOffset,
	setShouldScroll,
	animatedRef,
	panRef,
	children,
	isOverrideScrollingSV,
	dragArea,
}: DragSelectionProviderProps & {
	dragArea: SharedValue<DragArea>;
}) => {
	const safeArea = useSafeArea();
	const dim = useDim();

	// Handling top and bottom of page scrolling
	const scrollFrameCallback = useFrameCallback(({ timeSincePreviousFrame }) => {
		if (dragArea.value) {
			let scrollAdjustment = 0;

			const topLimit = safeArea.top + 0.2 * dim.height;
			const bottomLimit = safeArea.bottom + 0.2 * dim.height;
			const scrollIncrementAmount = 0.016 * (timeSincePreviousFrame ?? 0);
			const adaptiveScrollMax = 60;
			const adaptiveScrollOffset = 0.1 * dim.height;
			const minScrollFactor = 15;

			if (dragArea.value.proximityToTop && dragArea.value.proximityToTop < topLimit) {
				scrollAdjustment = -(
					scrollIncrementAmount *
					Math.max(
						Math.min(
							topLimit - adaptiveScrollOffset - dragArea.value.proximityToTop,
							adaptiveScrollMax,
						),
						minScrollFactor,
					)
				);
			} else if (
				dragArea.value.proximityToBottom &&
				dragArea.value.proximityToBottom < bottomLimit
			) {
				scrollAdjustment =
					scrollIncrementAmount *
					Math.max(
						Math.min(
							bottomLimit - adaptiveScrollOffset - dragArea.value.proximityToBottom,
							adaptiveScrollMax,
						),
						minScrollFactor,
					);
			}

			if (scrollAdjustment !== 0) {
				const scrollTarget = scrollOffset.value + scrollAdjustment;

				if (native) {
					isOverrideScrollingSV.value = true;
					scrollTo(animatedRef, 0, scrollTarget, false);
					scrollOffset.value = scrollTarget;
				} else {
					animatedRef.current?.scrollToOffset({
						offset: scrollTarget,
						animated: false,
					});
				}
			} else {
				isOverrideScrollingSV.value = false;
			}
		} else {
			isOverrideScrollingSV.value = false;
		}
	}, false);
	const setScrollFrameCb = useCallback(
		(value: boolean) => scrollFrameCallback.setActive(value),
		[scrollFrameCallback],
	);

	const animatedStyles = useAnimatedStyle(() => {
		if (dragArea.value === null) {
			return {
				display: "none",
			};
		}

		const springConfig: SpringConfig = {
			damping: 40,
			stiffness: 600,
		};

		return {
			display: "flex",
			left: withSpring(dragArea.value.left, springConfig),
			top: withSpring(dragArea.value.top, springConfig),
			width: withSpring(dragArea.value.width, springConfig),
			height: withSpring(dragArea.value.height, springConfig),
		};
	}, [dragArea]);

	// Handles adjusting dragArea based on scrollOffset
	useAnimatedReaction(
		() => {
			return scrollOffset.value;
		},
		(currentValue, previousValue) => {
			if (currentValue !== previousValue && dragArea.value !== null) {
				const diff = currentValue - (previousValue ?? 0);
				dragArea.value = {
					...dragArea.value,
					top: dragArea.value.top - diff,
					height: dragArea.value.height + diff,
				};
			}
		},
		[dragArea, scrollOffset],
	);

	const handleSelectionStart = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			runOnJS(haptic)("Heavy");
			const effectiveYOffset = native ? 0 : scrollOffset.value;
			dragArea.value = {
				startScrollOffset: scrollOffset.value,
				startX: event.x,
				startY: event.y - effectiveYOffset,
				endX: event.x,
				endY: event.y - effectiveYOffset,
				left: event.x,
				top: event.y - effectiveYOffset,
				width: 0,
				height: 0,
			};

			runOnJS(setShouldScroll)(false);
			runOnJS(setScrollFrameCb)(true);
		},
		[dragArea, scrollOffset, setShouldScroll, setScrollFrameCb],
	);

	const handleSelectionChange = useCallback(
		(
			event: GestureUpdateEvent<PanGestureHandlerEventPayload & PanGestureChangeEventPayload>,
		) => {
			"worklet";
			updateDragArea(dim.height, dragArea, event, scrollOffset);
		},
		[dim, dragArea, scrollOffset],
	);

	const handleSelectionFinalize = useCallback(() => {
		"worklet";
		dragArea.value = null;
		runOnJS(setShouldScroll)(true);
		runOnJS(setScrollFrameCb)(false);
		isOverrideScrollingSV.value = false;
	}, [dragArea, setShouldScroll, setScrollFrameCb, isOverrideScrollingSV]);

	const gesture = useMemo(() => {
		const panGestureWeb = Gesture.Pan();
		const panGestureNative = Gesture.Pan().activateAfterLongPress(200);

		return (native ? panGestureNative : panGestureWeb)
			.onStart(handleSelectionStart)
			.onChange(handleSelectionChange)
			.onFinalize(handleSelectionFinalize)
			.shouldCancelWhenOutside(false)
			.simultaneousWithExternalGesture(panRef)
			.enabled(enabled);
	}, [enabled, handleSelectionStart, handleSelectionChange, handleSelectionFinalize, panRef]);

	return (
		<>
			{enabled && (
				<Animated.View
					pointerEvents="none"
					style={[
						{
							backgroundColor: "white",
							position: FixedPosition,
							zIndex: 1000,
							opacity: 0.3,
							borderRadius: 8,
						},
						animatedStyles,
					]}
				/>
			)}
			<GestureDetector touchAction="pan-y" gesture={gesture}>
				{children}
			</GestureDetector>
		</>
	);
};

/**
 * Definitely will not enable drag selection throughout this session
 */
const DragSelectionProvider = ({
	children,
	enabled: enabledProp,
	...rest
}: DragSelectionProviderProps) => {
	const { device } = useDevice();
	const willNotEnable = useMemo(
		() => (isTouchScreen || device === "mobile") && !native,
		[device],
	);
	const enabled = useMemo(() => enabledProp && !willNotEnable, [enabledProp, willNotEnable]);
	const dragArea = useSharedValue<DragArea>(null);

	return (
		<DragSelectionWrapper dragArea={dragArea} enabled={enabled}>
			{willNotEnable ? (
				children
			) : (
				<DragSelectionProviderContent enabled={enabled} dragArea={dragArea} {...rest}>
					{children}
				</DragSelectionProviderContent>
			)}
		</DragSelectionWrapper>
	);
};

export default DragSelectionProvider;
